import dayjs from 'dayjs'
import * as productUnitType from '~/constants/productUnitType'
import { isNotEmpty } from '~/utils'

/**
 * PD_IMG_URL_LIST (JSON 문자열) → pdImgUrlList (배열) 파싱
 */
export function parsePdImgUrlList (raw) {
  const src = raw.PD_IMG_URL_LIST || raw.pdImgUrlList
  if (!src) { return undefined }
  if (Array.isArray(src)) { return src }
  try {
    const parsed = JSON.parse(src)
    return Array.isArray(parsed) ? parsed : undefined
  } catch (e) {
    return undefined
  }
}

export default class UnifiedModel {
  constructor (data) {
    Object.assign(this, data)
  }
}

function buildBaseData (raw) {
  return {
    appDdcatPdYn: raw.appDdcatPdYn,
    avgStscVal: raw.avgStscVal,
    bestYn: raw.bestYn,
    brndCd: raw.brndCd,
    brndNm: raw.brndNm,
    bunYn: raw.bunYn,
    buyLmtAgeCd: raw.buyLmtAgeCd,
    buyLmtAgeNm: raw.buyLmtAgeNm,
    dctnYn: raw.dctnYn,
    delDtm: raw.delDtm,
    delYn: raw.delYn,
    emblem: raw.emblem,
    engBrndNm: raw.engBrndNm,
    exhPdNm: raw.exhPdNm,
    exhYn: raw.exhYn,
    fdrmOrPsblYn: raw.fdrmOrPsblYn,
    gvawCnt: raw.gvawCnt,
    htrkCnvMummQy: raw.htrkCnvMummQy,
    jpQy: raw.jpQy,
    mappBoxPdNo: raw.mappBoxPdNo,
    massOrPsblYn: raw.massOrPsblYn,
    mrImgUrl: raw.mrImgUrl,
    mrNknmNm: raw.mrNknmNm,
    newPdYn: raw.newPdYn,
    onlSclCd: raw.onlSclCd,
    onlStckQy: raw.onlStckQy,
    onlyOnlYn: raw.onlyOnlYn,
    onlyQuickYn: raw.onlyQuickYn,
    optPdYn: raw.optPdYn,
    pdCttsTy: raw.pdCttsTy,
    pdDtlDc: raw.pdDtlDc,
    pdImgUrl: raw.pdImgUrl,
    pdImgUrlList: parsePdImgUrlList(raw),
    pdNm: raw.pdNm,
    pdNo: raw.pdNo,
    pdPrc: raw.pdPrc,
    pdTy: raw.pdTy,
    pdsOrPsblYn: raw.pdsOrPsblYn,
    epieceSleYn: raw.epieceSleYn,
    pkgYn: raw.pkgYn,
    pkupDdcatYn: raw.pkupDdcatYn,
    pkupOrPsblYn: raw.pkupOrPsblYn,
    quickOrPsblYn: raw.quickOrPsblYn,
    revwCnt: raw.revwCnt,
    sleStDt: raw.sleStDt,
    sleStsCd: raw.sleStsCd,
    sleStsNm: raw.sleStsNm,
    sleUtpc: raw.sleUtpc,
    soldOutYn: raw.soldOutYn,
    sortOrdr: raw.sortOrdr,
    strgyPdYn: raw.strgyPdYn,
    totOrQy: raw.totOrQy,
    wrhDt: raw.wrhDt,
    wrhParmDt: raw.wrhParmDt,
    onlLclNm: raw.onlLclNm,
    zzimYn: raw.zzimYn,
    checked: raw.checked,
    banner: raw.banner,
    banrLinkMvmnTyCd: raw.banrLinkMvmnTyCd,
    pcomLinkUrl: raw.pcomLinkUrl,
    banrRplcTextNm: raw.banrRplcTextNm,
    pcomIconAtchFileUrl: raw.pcomIconAtchFileUrl,
    revwAvg: raw.revwAvg,
    ancYn: raw.ancYn,
    skuYn: raw.skuYn,
    genderNm: raw.genderNm,
  }
}

export function normalizeResponse (raw, moduleType) {
  const baseData = buildBaseData(raw)

  switch (moduleType) {
    case productUnitType.DS_PRODUCT:
      return new UnifiedModel({
        ...baseData,
      })
    case productUnitType.PD_REVIEW_PRODUCT:
      return new UnifiedModel({
        ...baseData,
        best: false,
        pdAnc: false,
        optCn: '',
        optPdYn: raw.optPdYn === 'Y',
        pdImgUrl: raw.imgUrl,
        popupYn: true
      })
    case productUnitType.PD_PRODUCT:
      return new UnifiedModel({
        ...baseData,
        id: raw.toString(),
        video: raw.pdCttSty === '02' ? raw.pdImgUrl : '', // todo: video 옵션이 들어가야함
        soldPlan: (raw.sleStsCd === '02' && raw.wrhParmDt !== '') ? raw.wrhParmDt : '', // 판매예정일
        onlSclCd: isNotEmpty(raw.ONL_SCL_CD) ? raw.ONL_SCL_CD : raw.onlSclCd,
        strgyPdYn: isNotEmpty(raw.strgyPdYn) ? raw.strgyPdYn : raw.STRGY_PD_YN ?? '', // 오픈런 전략상품 여부
        sleStDt: isNotEmpty(raw.sleStDtm) ? raw.sleStDtm : (isNotEmpty(raw.sleStDt) ? raw.sleStDt : raw.SLE_ST_DTM ?? ''), // 오픈런 판매시작 일시
        jpQy: isNotEmpty(raw.jpQy) ? Number(raw.jpQy) : Number(raw.JP_QY),
        isBoxPd: raw.pdTy === '06', // 박스상품여부
        mappBoxPdNo: isNotEmpty(raw.mappBoxPdNo) ? raw.mappBoxPdNo : raw.MAPP_BOX_PD_NO, // 박스매핑된상품번호
        isOpenRun: !!(raw.strgyPdYn && dayjs(new Date()).format('YYYYMMDDHH') < raw.sleStDt),
        quickOrPsblYn: isNotEmpty(raw.quickOrPsblYn) ? raw.quickOrPsblYn : raw.QUICK_OR_PSBL_YN ?? 'N', // 오늘 배송가능여부
        ntcnRqstDtm: raw.ntcnRqstDtm, // 재입고알림시작일
        ntcnEndDtm: raw.ntcnEndDtm, // 재입고알림종료일
        optCn: isNotEmpty(raw.optCn) ? raw.optCn : raw.OPT_CN, // 옵션명
        onldPdNo: raw.onldPdNo,
        strCd: raw.strCd,
        strNm: raw.strNm,
        optPdYn: isNotEmpty(raw.optPdYn) ? raw.optPdYn : (isNotEmpty(raw.skuYn) ? raw.skuYn : 'N'),
        rcmPrsThumbCn: isNotEmpty(raw.BLUX_RCM_PRS_CN) ? raw.BLUX_RCM_PRS_CN.split('||')[1]?.substring(3) : isNotEmpty(raw.rcmPrsThumbCn) ? raw.rcmPrsThumbCn : '',
        rcmPrsCn: isNotEmpty(raw.BLUX_RCM_PRS_CN) ? raw.BLUX_RCM_PRS_CN.split('||')[0]?.substring(3) : isNotEmpty(raw.rcmPrsCn) ? raw.rcmPrsCn : '',
        genderNm: isNotEmpty(raw.genderNm) ? raw.genderNm : setGenderName(raw)
      })
    case productUnitType.MS_PRODUCT:
      return new UnifiedModel({
        ...baseData,
        pdImgUrl: isNotEmpty(raw.ATCH_FILE_URL) ? process.env.CDN_BASE_URL + raw.ATCH_FILE_URL : (isNotEmpty(raw.pdImgUrl) ? process.env.CDN_BASE_URL + raw.pdImgUrl : ''),
        pdPrc: raw.PD_PRC || raw.pdPrc || raw.price,
        exhPdNm: raw.EXH_PD_NM || raw.exhPdNm,
        soldOutYn: false,
        pdNo: raw.PD_NO || raw.pdNo,
        onlyPkupYn: raw.ONLY_PKUP_YN === 'Y' || raw.pkupDdcatYn === 'Y',
        optPdYn: raw.OPT_PD_YN || raw.optPdYn === 'Y',
        onlSclCd: raw.ONL_SCL_CD || raw.onlSclCd,
        newPdYn: raw.NEW_PD_YN || raw.newPdYn,
        pdsOrPsblYn: raw.PDS_OR_PSBL_YN === 'Y' || raw.pdsOrPsblYn === 'Y',
        pdNm: raw.EXH_PD_NM || raw.exhPdNm,
        sleStsCd: raw.SLE_STS_CD || raw.sleStsCd,
        wrhDt: raw.WRH_DT || raw.wrhDt,
      })
    case productUnitType.CS_PRODUCT:
      return new UnifiedModel({
        ...baseData,
        orNo: raw.orNo || raw.or_no,
        pdNo: raw.PD_NO || raw.pdNo,
        pdPrc: raw.orAm || raw.pdPrc || raw.PD_PRC,
        buying: raw.orQy || raw.OR_QY || raw.totOrQy,
        orQy: raw.orQy || raw.OR_QY,
        optCn: raw.orTyCd === '03' ? 'random' : raw.optCn,
        exhPdNm: raw.exhPdNm ? raw.exhPdNm : raw.EXH_PD_NM,
        dctnYn: raw.dctnYn || raw.DCTN_YN,
        pdImgUrl: isNotEmpty(raw.ATCH_FILE_URL) ? process.env.CDN_BASE_URL + raw.ATCH_FILE_URL : isNotEmpty(raw.pdImgUrl) ? process.env.CDN_BASE_URL + raw.pdImgUrl : '/images/content/noimage.png',
        checked: raw.checked || raw.CHECKED,
        soldOut: raw.SOLD_OUT_YN || raw.soldOutYn || raw.soldOut,
        sleStsCd: raw.sleStsCd || raw.SLE_STS_CD,
        offlPdYn: raw.OFFL_PD_YN,

      })
    default:
      return new UnifiedModel(raw)
  }
}

// 속성값에서 성별값 추출하기
export function setGenderName (obj) {
  const genderArr = ['남녀공용', '남성', '여성', '아동']
  if (isNotEmpty(obj.ATTR_ITEM_NM)) { // 검색결과에 속성값이 있는 경우
    if (isNotEmpty(obj.ONL_SCL_CD) && obj.ONL_SCL_CD.startsWith('J')) { // 카테고리가 J(패션/잡화)인경우
      // '|'로 속성을 나누고, 각 속성을 '>'로 분할하여 실제 속성값 명칭만 추출합니다.
      const attrValues = obj.ATTR_ITEM_NM.split('|').map((item) => {
        const parts = item.split('>')
        return parts[parts.length - 1] || ''
      })

      // 추출한 속성값 명칭들을 순서대로 검사하여 가장 처음 매칭되는 성별을 반환합니다.(성별속성 2개이상 입력시 우선순위대로 정렬한 첫 성별속성만 리턴.)
      for (const val of attrValues) {
        const matched = genderArr.find(keyword => val.includes(keyword))
        if (isNotEmpty(matched)) {
          return matched
        }
      }
    }
  }
  return ''
}
